import { resolveSchema, resolveDomainEvent } from './workflowSpec.js'
import { computeMidString, getLastCardSortkey } from './sortkey.js'
import {
  buildSchemaField,
  resolveEntityRef,
  resolveFieldRefs,
  resolveFieldUpdateRefs,
  applyNestedFieldUpdates,
  buildSchemaRef,
  unionSchemaFields
} from './schemaFields.js'
import { sanitizeCardDescription } from './sanitize.js'
import { buildCardDeletes } from './cardMutations.js'
import { DomainModelRole } from '../constants/cardTypes.js'
import { SchemaType } from '../constants/schema.js'

const generateId = () => globalThis.crypto.randomUUID()

const findCardsLinkedToSchema = (domainEvents, schemaId, cardTypesMap) => {
  const matches = []

  for (let eventIndex = 0; eventIndex < domainEvents.length; eventIndex++) {
    const event = domainEvents[eventIndex]
    const cards = event.cards || []
    for (let cardIndex = 0; cardIndex < cards.length; cardIndex++) {
      const card = cards[cardIndex]
      const role = cardTypesMap[card.cardTypeId]?.domainModelRole
      if ((role === DomainModelRole.DOMAIN_EVENT || role === DomainModelRole.READ_MODEL) && card.schemaId === schemaId) {
        matches.push({ card, event, eventIndex, cardIndex })
      }
    }
  }

  return matches
}

export const buildCreateDomainEventSchemasMutation = ({ workflow, workflowId, domainEventSchemas, cardTypes }) => {
  if (!Array.isArray(domainEventSchemas) || domainEventSchemas.length === 0) {
    return { error: 'domainEventSchemas is required and must be a non-empty array' }
  }

  const cardTypesList = cardTypes || []
  const domainEventCardType = cardTypesList.find((cardType) => cardType.domainModelRole === DomainModelRole.DOMAIN_EVENT)
  if (!domainEventCardType) {
    return { error: 'Domain Event card type not found in this workflow' }
  }
  const cardTypesMap = Object.fromEntries(cardTypesList.map((cardType) => [cardType.id, cardType]))

  const existingSchemasByName = new Map(
    (workflow.schemas || [])
      .filter((schema) => schema.type === SchemaType.DOMAIN_EVENT)
      .map((schema) => [schema.name, schema])
  )
  // Per name, either a new schema being built or an existing one being merged into, as create_read_models does.
  const nameToState = new Map()
  const batchEventIds = new Set()

  const domainEvents = workflow.domainEvents || []
  let lastSortkey = getLastCardSortkey(domainEvents)

  const pendingNewCards = []
  const pendingCardLinks = []

  for (const entry of domainEventSchemas) {
    const { domainEvent, name, entity, fields } = entry || {}

    if (!domainEvent) {
      return { error: 'Each domain event schema must have a domainEvent ref' }
    }
    if (!name || typeof name !== 'string' || name.trim() === '') {
      return { error: 'Each domain event schema must have a non-empty name' }
    }

    const sanitizedName = sanitizeCardDescription(name)

    const matchedEvent = resolveDomainEvent(domainEvent, workflow)
    if (!matchedEvent) {
      return { error: `Domain event not found for ref: ${domainEvent}` }
    }

    if (batchEventIds.has(matchedEvent.id)) {
      return { error: `Multiple domain event schemas target the same domain event "${matchedEvent.description}" in the batch` }
    }
    batchEventIds.add(matchedEvent.id)

    const existingDomainEventCard = (matchedEvent.cards || []).find((card) => {
      return cardTypesMap[card.cardTypeId]?.domainModelRole === DomainModelRole.DOMAIN_EVENT
    })
    if (existingDomainEventCard?.schemaId) {
      return { error: `Event "${matchedEvent.description}" already has a Domain Event schema. Use update_domain_event_schema to modify it.` }
    }

    const resolvedEntityId = entity ? resolveEntityRef(entity, workflow) : null
    const resolvedFields = resolveFieldRefs(fields, workflow)
    const incomingFields = (resolvedFields || []).map((field) => buildSchemaField(field, SchemaType.DOMAIN_EVENT))

    let state = nameToState.get(sanitizedName)
    if (!state) {
      const existingSchema = existingSchemasByName.get(sanitizedName)
      state = existingSchema
        ? { mode: 'existing', schema: existingSchema, schemaId: existingSchema.id, entityId: existingSchema.entityId, mergedFields: existingSchema.fields || [] }
        : { mode: 'new', schemaId: generateId(), entityId: null, mergedFields: [] }
      nameToState.set(sanitizedName, state)
    }
    state.mergedFields = unionSchemaFields(state.mergedFields, incomingFields)
    if (resolvedEntityId && !state.entityId) {
      state.entityId = resolvedEntityId
    }
    const { schemaId } = state

    if (existingDomainEventCard) {
      // The event already has a Domain Event card with no schema — link it to the schema.
      pendingCardLinks.push({
        eventId: matchedEvent.id,
        card: existingDomainEventCard,
        cardIndex: (matchedEvent.cards || []).indexOf(existingDomainEventCard),
        schemaId
      })
    } else {
      const sortkey = computeMidString(lastSortkey, '')
      lastSortkey = sortkey

      pendingNewCards.push({
        eventId: matchedEvent.id,
        name,
        schemaId,
        sortkey
      })
    }
  }

  const newSchemas = []
  const changedSchemas = []
  for (const [name, state] of nameToState) {
    if (state.mode === 'new') {
      newSchemas.push({ id: state.schemaId, name, type: SchemaType.DOMAIN_EVENT, entityId: state.entityId, fields: state.mergedFields, index: -1 })
    } else if (JSON.stringify(state.mergedFields) !== JSON.stringify(state.schema.fields || []) || state.entityId !== state.schema.entityId) {
      changedSchemas.push({
        ...state.schema,
        entityId: state.entityId,
        fields: state.mergedFields,
        index: workflow.schemas.findIndex((schema) => schema.id === state.schemaId),
        expectedVersion: state.schema.version || null
      })
    }
  }
  const schemas = [...newSchemas, ...changedSchemas]
  const linkedNames = [...nameToState].filter(([, state]) => state.mode === 'existing').map(([name]) => name)

  const schemaInput = schemas.length > 0
    ? {
        id: workflowId,
        expectedVersion: workflow.version,
        schemas,
        updateNote: {
          action: 'created domain event schemas',
          target: schemas.map((schema) => schema.name).join(', ')
        }
      }
    : null

  const cards = []

  for (const { eventId, name, schemaId, sortkey } of pendingNewCards) {
    const eventIndex = domainEvents.findIndex((event) => event.id === eventId)
    cards.push({
      description: name,
      done: false,
      cardTypeId: domainEventCardType.id,
      releaseNo: null,
      sortkey,
      boundedContext: null,
      schemaId,
      cardIndex: -1,
      eventId,
      eventIndex
    })
  }

  for (const { eventId, card, cardIndex, schemaId } of pendingCardLinks) {
    const eventIndex = domainEvents.findIndex((event) => event.id === eventId)
    cards.push({
      cardId: card.id,
      cardIndex,
      expectedVersion: card.version,
      description: card.description,
      sortkey: card.sortkey,
      cardTypeId: card.cardTypeId,
      done: card.done ?? false,
      schemaId,
      boundedContext: card.boundedContext,
      cardinality: card.cardinality,
      releaseNo: card.releaseNo,
      eventId,
      eventIndex
    })
  }

  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  const cardInput = cards.length > 0
    ? {
        workflowId,
        updateNote: {
          action: 'added domain event cards',
          target: [...nameToState.keys()].join(', ')
        },
        cards
      }
    : null

  const createdNote = `${newSchemas.length} ${newSchemas.length === 1 ? 'domain event schema' : 'domain event schemas'} created successfully`
  const linkedNote = `Linked existing domain event schema${linkedNames.length === 1 ? '' : 's'}: ${linkedNames.join(', ')}`
  let message = createdNote
  if (linkedNames.length > 0) {
    message = newSchemas.length === 0 ? `No new domain event schemas needed. ${linkedNote}` : `${createdNote}. ${linkedNote}`
  }

  return {
    schemaInput,
    cardInput,
    newSchemas,
    message,
    refs: [...nameToState.keys()].map((name) => ({ name, ref: buildSchemaRef('domainEvents', name) }))
  }
}

export const buildUpdateDomainEventSchemasMutation = ({ workflow, workflowId, domainEventSchemas, cardTypes }) => {
  if (!Array.isArray(domainEventSchemas) || domainEventSchemas.length === 0) {
    return { error: 'domainEventSchemas is required and must be a non-empty array' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const claimedSchemaIds = new Set()
  const claimedNewNames = new Set()
  const schemaInputs = []
  const changeSummaries = []
  const refsOut = []
  const pendingRenames = []

  for (const update of domainEventSchemas) {
    const {
      domainEventSchema: schemaRef,
      name,
      entity,
      addFields: rawAdds,
      updateFields: rawUpdates,
      removeFields
    } = update || {}

    if (!schemaRef) {
      return { error: 'Each item must include a domainEventSchema ref' }
    }

    const resolved = resolveSchema(schemaRef, workflow, cardTypesMap)
    if (!resolved) {
      return { error: `Domain event schema not found for ref: ${schemaRef}` }
    }
    const schemaId = resolved.id

    if (claimedSchemaIds.has(schemaId)) {
      return { error: `Domain event schema "${schemaRef}" appears more than once in the batch` }
    }
    claimedSchemaIds.add(schemaId)

    const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
    if (schemaIndex === -1) {
      return { error: `Domain event schema not found for ref: ${schemaRef}` }
    }

    const addFields = resolveFieldRefs(rawAdds, workflow)
    const updateFields = resolveFieldUpdateRefs(rawUpdates, workflow)

    const schema = workflow.schemas[schemaIndex]
    const currentFields = [...(schema.fields || [])]
    const changes = []

    if (removeFields && removeFields.length > 0) {
      for (const fieldName of removeFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in domain event schema "${schema.name}"` }
        }
        currentFields.splice(fieldIndex, 1)
        changes.push(`removed "${fieldName}"`)
      }
    }

    if (updateFields && updateFields.length > 0) {
      for (const { fieldName, updates } of updateFields) {
        const fieldIndex = currentFields.findIndex((field) => field.name === fieldName)
        if (fieldIndex === -1) {
          return { error: `Field "${fieldName}" not found in domain event schema "${schema.name}"` }
        }

        if (updates.name && updates.name !== fieldName) {
          const collision = currentFields.find((field) => field.name === updates.name)
          if (collision) {
            return { error: `Field "${updates.name}" already exists in domain event schema "${schema.name}"` }
          }
        }

        const updatedField = { ...currentFields[fieldIndex] }

        if (updates.name !== undefined) {
          updatedField.name = updates.name
        }
        if (updates.relatedEntityId !== undefined) {
          updatedField.relatedEntityId = updates.relatedEntityId
          if (!updatedField.relatedEntityId) {
            delete updatedField.fields
          }
        }
        if (updates.cardinality !== undefined) {
          updatedField.cardinality = updates.cardinality
        }
        if (updates.fields !== undefined) {
          const nested = applyNestedFieldUpdates(updatedField.fields, updates.fields)
          if (nested && nested.length > 0) {
            updatedField.fields = nested
          } else {
            delete updatedField.fields
          }
        }

        currentFields[fieldIndex] = updatedField
        changes.push(`updated "${fieldName}"`)
      }
    }

    if (addFields && addFields.length > 0) {
      for (const field of addFields) {
        if (currentFields.find((existing) => existing.name === field.name)) {
          return { error: `Field "${field.name}" already exists in domain event schema "${schema.name}"` }
        }
        currentFields.push(buildSchemaField(field, SchemaType.DOMAIN_EVENT))
        changes.push(`added "${field.name}"`)
      }
    }

    const updatedSchema = {
      ...schema,
      fields: currentFields
    }

    const sanitizedName = name !== undefined ? sanitizeCardDescription(name) : undefined
    if (sanitizedName !== undefined && sanitizedName !== schema.name) {
      const collision = workflow.schemas.find((other) => other.name === sanitizedName && other.type === SchemaType.DOMAIN_EVENT && other.id !== schemaId)
      if (collision) {
        return { error: `Domain event schema "${sanitizedName}" already exists` }
      }
      if (claimedNewNames.has(sanitizedName)) {
        return { error: `Domain event schema "${sanitizedName}" is set by more than one entry in the batch` }
      }
      claimedNewNames.add(sanitizedName)
      updatedSchema.name = sanitizedName
      changes.push(`renamed to "${sanitizedName}"`)
      pendingRenames.push({ schemaId, newName: sanitizedName })
    }

    if (entity !== undefined) {
      updatedSchema.entityId = entity ? resolveEntityRef(entity, workflow) : null
      changes.push('entity updated')
    }

    if (changes.length === 0) {
      return { error: `No changes specified for "${schema.name}"` }
    }

    schemaInputs.push({
      ...updatedSchema,
      index: schemaIndex,
      expectedVersion: schema.version || null
    })
    changeSummaries.push(`"${schema.name}": ${changes.join(', ')}`)
    refsOut.push({
      name: updatedSchema.name,
      ref: buildSchemaRef('domainEvents', updatedSchema.name)
    })
  }

  const schemaInput = {
    id: workflowId,
    expectedVersion: workflow.version,
    schemas: schemaInputs,
    updateNote: {
      action: 'updated domain event schemas',
      target: schemaInputs.map((schema) => schema.name).join(', ')
    }
  }

  // cardInput's expectedVersion is threaded in by the caller after schemaInput persists.
  let cardInput = null

  if (pendingRenames.length > 0) {
    const cardsToUpdate = []

    for (const { schemaId, newName } of pendingRenames) {
      for (const match of findCardsLinkedToSchema(workflow.domainEvents || [], schemaId, cardTypesMap)) {
        cardsToUpdate.push({
          cardId: match.card.id,
          cardIndex: match.cardIndex,
          expectedVersion: match.card.version,
          description: newName,
          sortkey: match.card.sortkey,
          cardTypeId: match.card.cardTypeId,
          done: match.card.done ?? false,
          schemaId: match.card.schemaId,
          boundedContext: match.card.boundedContext,
          releaseNo: match.card.releaseNo,
          eventId: match.event.id,
          eventIndex: match.eventIndex
        })
      }
    }

    if (cardsToUpdate.length > 0) {
      cardInput = {
        workflowId,
        updateNote: {
          action: 'renamed domain event cards',
          target: pendingRenames.map((rename) => rename.newName).join(', ')
        },
        cards: cardsToUpdate
      }
    }
  }

  const updatedCount = schemaInput.schemas.length

  return {
    schemaInput,
    cardInput,
    changeSummaries,
    message: `${updatedCount} ${updatedCount === 1 ? 'domain event schema' : 'domain event schemas'} updated: ${changeSummaries.join(' | ')}`,
    refs: refsOut
  }
}

export const buildDeleteDomainEventSchemaMutation = ({ workflow, workflowId, domainEventSchema, cardTypes }) => {
  if (!domainEventSchema) {
    return { error: 'domainEventSchema ref is required' }
  }

  const cardTypesMap = Object.fromEntries((cardTypes || []).map((cardType) => [cardType.id, cardType]))

  const resolved = resolveSchema(domainEventSchema, workflow, cardTypesMap)
  if (!resolved) {
    return { error: `Domain event schema not found for ref: ${domainEventSchema}` }
  }
  if (resolved.type !== SchemaType.DOMAIN_EVENT) {
    return { error: `"${domainEventSchema}" is not a domain event schema, so nothing was deleted. A read model is deleted with delete_read_model, an entity with delete_entity` }
  }
  const schemaId = resolved.id

  const schemaIndex = (workflow.schemas || []).findIndex((schema) => schema.id === schemaId)
  if (schemaIndex === -1) {
    return { error: 'Domain event schema not found' }
  }

  const schema = workflow.schemas[schemaIndex]
  const matches = findCardsLinkedToSchema(workflow.domainEvents || [], schemaId, cardTypesMap)

  // Every card linked to the schema is deleted first, on any event it is shown on;
  // each card delete and the schema delete are version-threaded by the caller.
  const cardDeletes = buildCardDeletes(workflowId, matches)

  const schemaInput = {
    id: workflowId,
    deletedSchemas: [{ deleteIndex: schemaIndex, deleteId: schemaId }],
    updateNote: {
      action: 'deleted domain event schema',
      target: schema.name
    }
  }

  return { cardDeletes, schemaInput, schema, message: `Domain event schema "${schema.name}" deleted successfully` }
}
